// ExportBar — one reusable strip that gives ANY screen: Excel · PDF · Email · WhatsApp · Schedule.
//
//   <ExportBar
//     reportKey="bd-in-hand"                       // stable id (used for filenames + schedules)
//     title="Bangladesh — Orders in Hand"
//     subtitle="Buyer: ALL | search: 'abc'"        // the active filters, in words (optional)
//     sections={[{ name:'Orders', columns:[{key,label,type,total}], rows }]}
//     source={{ endpoint:'/orders/bd-list', params:{bucket:'in_hand'}, path:'', filter:{...} }}  // optional → enables Schedule
//   />
//
// rows = exactly what the screen is showing (after its filters), so export == view.
// `source` tells the server how to re-fetch the same data at schedule time; screens whose
// rows are computed only in the browser simply omit it and get everything except Schedule.
import React, { useEffect, useMemo, useState } from 'react';
import GmtTrainingModal from '../pages/GmtTrainingModal';

export function isGmtReport(reportKey, videoStage) {
  if (videoStage === 0 || videoStage === false) return false;
  if (videoStage != null && Number(videoStage) >= 1) return true;
  const rk = String(reportKey || '').toLowerCase();
  return (
    rk === 'daily-production' ||
    rk.startsWith('factory-') ||
    rk.startsWith('fab-inhouse') ||
    rk.startsWith('cost-sheet') ||
    rk.startsWith('final-cost') ||
    rk.startsWith('shipment') ||
    rk.includes('gmt')
  );
}

export function resolveVideoStage(reportKey, videoStage) {
  if (videoStage != null && Number(videoStage) >= 1) return Number(videoStage);
  const rk = String(reportKey || '').toLowerCase();
  if (rk.includes('production')) return 2; // Daily Production Entry (Form 5582)
  if (rk.includes('fabinhouse') || rk.includes('fab-inhouse') || rk.includes('fabric')) return 3; // Fabric Inhouse
  if (rk.includes('costsheet') || rk.includes('cost-sheet') || rk.includes('sales-cost')) return 4; // Sales Contract Cost Sheet
  if (rk.includes('final')) return 5; // Final Costing & Post-Production Variance Audit
  if (rk.includes('shipment')) return 6; // Shipment Creation & Ex-Factory Handover
  return 1; // Stage 1: Factory Portal Architecture & 5 Core Modules / Central War Room
}

// Channel switches. Email goes out through the company's registered
// noreply.mindforgeerp@gmail.com Database Mail profile. WhatsApp stays off until a
// WhatsApp sender is actually linked for Rishab — the code behind it is intact.
export const SHOW_EMAIL = true;
export const SHOW_WHATSAPP = false;

// Scheduling is a granted right (reports.schedule); management implies it. The
// server enforces this on every schedule route — this only decides whether the
// button is worth showing.
export function canSchedule() {
  const u = user();
  return !!(u.isManagement || (Array.isArray(u.rights) && u.rights.includes('reports.schedule')));
}

const API = '/api';
const tok = () => localStorage.getItem('dj_token');
const user = () => { try { return JSON.parse(localStorage.getItem('dj_user')) || {}; } catch { return {}; } };

async function post(path, body) {
  const r = await fetch(API + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` }, body: JSON.stringify(body) });
  if (!r.ok) { let m = `HTTP ${r.status}`; try { m = (await r.json()).error || m; } catch {} throw new Error(m); }
  return r;
}
export async function apiGet(path) {
  const r = await fetch(API + path, { headers: { Authorization: `Bearer ${tok()}` } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

// Row values may be computed in the view; allow columns to carry a `get(row)` accessor and
// flatten those into plain values before posting (server only understands plain keys).
function materialise(sections) {
  return sections.map(s => ({
    name: s.name,
    columns: s.columns.map(({ key, label, type, total }) => ({ key, label, type: type || 'text', total: !!total })),
    rows: (s.rows || []).map(r => {
      const o = {};
      for (const c of s.columns) o[c.key] = c.get ? c.get(r) : r[c.key];
      return o;
    }),
  }));
}

function download(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 2000);
}

const btn = (active) => ({
  padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border)', background: active ? 'var(--gold)' : 'var(--bg2)',
  color: active ? 'var(--on-gold)' : 'var(--text2)', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, cursor: 'pointer', whiteSpace: 'nowrap',
});

export default function ExportBar({ reportKey, title, subtitle, sections, source, compact, videoStage, onVideoGuide, showVideo, factory }) {
  const [busy, setBusy] = useState('');
  const [modal, setModal] = useState(null);   // 'send' | 'schedule'
  const [msg, setMsg] = useState('');
  const [videoOpen, setVideoOpen] = useState(false);
  const rows = useMemo(() => (sections || []).reduce((n, s) => n + (s.rows ? s.rows.length : 0), 0), [sections]);
  const specBody = () => ({ report_key: reportKey, title, subtitle: subtitle || '', sections: materialise(sections || []) });
  const shouldShowVideo = showVideo !== undefined ? !!showVideo : isGmtReport(reportKey, videoStage);

  const exportAs = async (format) => {
    setBusy(format); setMsg('');
    try {
      const r = await post('/reports/export', { ...specBody(), format, filename: reportKey });
      const cd = r.headers.get('Content-Disposition') || '';
      const name = (/filename="([^"]+)"/.exec(cd) || [])[1] || `${reportKey}.${format}`;
      download(await r.blob(), name);
    } catch (e) { setMsg(`Export failed: ${e.message}`); }
    finally { setBusy(''); }
  };

  return (
    <div className="export-bar" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: compact ? '6px 12px' : '8px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
      <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, marginRight: 4 }}>REPORT · {rows.toLocaleString('en-IN')} rows</span>
      <button style={btn()} disabled={!!busy} onClick={() => exportAs('xlsx')}>{busy === 'xlsx' ? '…' : '📊 EXCEL'}</button>
      <button style={btn()} disabled={!!busy} onClick={() => exportAs('pdf')}>{busy === 'pdf' ? '…' : '📄 PDF'}</button>
      {SHOW_EMAIL && <button style={btn()} onClick={() => setModal('send')}>✉️ EMAIL</button>}
      {shouldShowVideo && (
        <button
          type="button"
          style={{
            ...btn(),
            background: 'linear-gradient(135deg, #1A4F9C 0%, #0d2b5c 100%)',
            color: '#ffffff',
            borderColor: '#1A4F9C',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5
          }}
          onClick={() => {
            if (onVideoGuide) onVideoGuide();
            else setVideoOpen(true);
          }}
          title="Watch Step-by-Step Video Guide for this Screen (English & Hindi)"
        >
          <span>🎥</span>
          <span>VIDEO GUIDE</span>
        </button>
      )}
      {SHOW_WHATSAPP && <button style={btn()} onClick={() => setModal('send-wa')}>🟢 WHATSAPP</button>}
      {source && source.endpoint && canSchedule() && <button style={btn()} onClick={() => setModal('schedule')}>⏰ SCHEDULE</button>}
      {msg && <span style={{ fontSize: 11, color: '#c0392b' }}>{msg}</span>}
      {videoOpen && (
        <GmtTrainingModal
          initialStage={resolveVideoStage(reportKey, videoStage)}
          factory={factory}
          onClose={() => setVideoOpen(false)}
        />
      )}
      {(modal === 'send' || modal === 'send-wa') && (
        <SendModal spec={specBody()} defaultChannel={modal === 'send-wa' ? 'whatsapp' : 'email'} onClose={() => setModal(null)} />
      )}
      {modal === 'schedule' && (
        <ScheduleModal spec={specBody()} source={source} reportKey={reportKey} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- shared modal chrome
function Modal({ title, onClose, children, width = 460 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', color: 'var(--text)', borderRadius: 14, width: '100%', maxWidth: width, maxHeight: '92vh', overflow: 'auto', padding: 18, boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 800, letterSpacing: .5 }}>{title}</div>
          <button onClick={onClose} style={{ ...btn(), padding: '3px 8px' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
const field = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' };
const label = { fontSize: 10, letterSpacing: 1, color: 'var(--text3)', margin: '10px 0 4px', textTransform: 'uppercase' };
const Chip = ({ on, onClick, children }) => <button type="button" onClick={onClick} style={{ ...btn(on), marginRight: 6, marginBottom: 6 }}>{children}</button>;

function useRecipients() {
  const [list, setList] = useState([]);
  useEffect(() => { apiGet('/reports/recipients').then(setList).catch(() => {}); }, []);
  return list;
}
function RecipientPicker({ people, onPick, phone }) {
  const opts = people.filter(p => phone ? p.phone : true);
  if (!opts.length) return null;
  return (
    <select style={{ ...field, marginTop: 4 }} value="" onChange={e => { if (e.target.value) onPick(e.target.value); }}>
      <option value="">+ add from team…</option>
      {opts.map(p => <option key={p.username} value={phone ? p.phone : p.username}>{p.name}{phone ? ` (${p.phone})` : ''}</option>)}
    </select>
  );
}
const addTo = (cur, v) => (cur ? cur.replace(/[,\s]+$/, '') + ', ' : '') + v;

// ---------------------------------------------------------------- Send now
function SendModal({ spec, defaultChannel, onClose }) {
  const me = user();
  const people = useRecipients();
  const [channels, setChannels] = useState({ email: defaultChannel === 'email' || !SHOW_WHATSAPP, whatsapp: SHOW_WHATSAPP && defaultChannel === 'whatsapp' });
  const [formats, setFormats] = useState({ pdf: true, xlsx: false });
  const [emailTo, setEmailTo] = useState(localStorage.getItem('dj_last_email_to') || '');
  const [waTo, setWaTo] = useState(localStorage.getItem('dj_last_wa_to') || (me.username && /^\d{10}$/.test(me.username) ? me.username : ''));
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const go = async () => {
    setBusy(true); setErr(''); setResult(null);
    try {
      const body = { ...spec, formats: Object.keys(formats).filter(k => formats[k]), channels: Object.keys(channels).filter(k => channels[k]), emailTo, waTo, message };
      const r = await (await post('/reports/send', body)).json();
      localStorage.setItem('dj_last_email_to', emailTo); localStorage.setItem('dj_last_wa_to', waTo);
      setResult(r);
      // WhatsApp without a linked sender → open the ready-made wa.me link right away (one per number).
      (r.whatsapp || []).filter(w => w.status === 'MANUAL' && w.link).forEach((w, i) => setTimeout(() => window.open(w.link, '_blank'), i * 400));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`SEND · ${spec.title}`} onClose={onClose}>
      {SHOW_WHATSAPP && <>
        <div style={label}>Channel</div>
        <Chip on={channels.email} onClick={() => setChannels(c => ({ ...c, email: !c.email }))}>✉️ Email</Chip>
        <Chip on={channels.whatsapp} onClick={() => setChannels(c => ({ ...c, whatsapp: !c.whatsapp }))}>🟢 WhatsApp</Chip>
      </>}
      <div style={label}>Attach</div>
      <Chip on={formats.pdf} onClick={() => setFormats(f => ({ ...f, pdf: !f.pdf }))}>📄 PDF</Chip>
      <Chip on={formats.xlsx} onClick={() => setFormats(f => ({ ...f, xlsx: !f.xlsx }))}>📊 Excel</Chip>
      {channels.email && <>
        <div style={label}>Email to (comma separated)</div>
        <input style={field} value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="name@company.com, other@company.com" />
      </>}
      {channels.whatsapp && <>
        <div style={label}>WhatsApp numbers (10-digit or with country code)</div>
        <input style={field} value={waTo} onChange={e => setWaTo(e.target.value)} placeholder="98xxxxxxxx, 91xxxxxxxxxx" />
        <RecipientPicker people={people} phone onPick={v => setWaTo(cur => addTo(cur, v))} />
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>The report is uploaded and a WhatsApp chat opens with the summary + secure download link, one per number.</div>
      </>}
      <div style={label}>Message (optional)</div>
      <textarea style={{ ...field, minHeight: 60 }} value={message} onChange={e => setMessage(e.target.value)} />
      {err && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 8 }}>{err}</div>}
      {result && (
        <div style={{ fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
          {result.email && (result.email.sent ? <div>✅ Email sent to {result.email.to.join(', ')}</div> : <div>❌ Email: {result.email.error}</div>)}
          {(result.whatsapp || []).map((w, i) => w.number ? (
            <div key={i}>{w.status === 'SENT' ? '✅' : w.status === 'FAILED' ? '❌' : '🟢'} {w.number} {w.status === 'MANUAL' && <a href={w.link} target="_blank" rel="noreferrer">open WhatsApp</a>}{w.error ? ` — ${w.error}` : ''}</div>
          ) : <div key={i}>❌ {w.error}</div>)}
          {(result.links || []).map(l => <div key={l.token}>🔗 <a href={l.url} target="_blank" rel="noreferrer">{l.filename}</a></div>)}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button style={btn()} onClick={onClose}>{result ? 'CLOSE' : 'CANCEL'}</button>
        {!result && <button style={btn(true)} disabled={busy || (!channels.email && !channels.whatsapp)} onClick={go}>{busy ? 'SENDING…' : 'SEND'}</button>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Schedule
export function ScheduleModal({ spec, source, reportKey, onClose, existing }) {
  const me = user();
  const people = useRecipients();
  const [frequency, setFrequency] = useState(existing?.frequency || 'daily');
  const [runTime, setRunTime] = useState(existing?.run_time || '09:00');
  const [weekday, setWeekday] = useState(existing?.weekday ?? 1);
  const [dom, setDom] = useState(existing?.day_of_month ?? 1);
  const [channels, setChannels] = useState({ email: existing ? existing.channels.includes('email') : true, whatsapp: SHOW_WHATSAPP && !!(existing && existing.channels.includes('whatsapp')) });
  const [formats, setFormats] = useState({ pdf: existing ? existing.formats.includes('pdf') : true, xlsx: existing ? existing.formats.includes('xlsx') : false });
  const [emailTo, setEmailTo] = useState(existing?.email_to || localStorage.getItem('dj_last_email_to') || '');
  const [waTo, setWaTo] = useState(existing?.wa_to || localStorage.getItem('dj_last_wa_to') || (me.username && /^\d{10}$/.test(me.username) ? me.username : ''));
  const [message, setMessage] = useState(existing?.message || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  const go = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        report_key: reportKey, title: spec.title, subtitle: spec.subtitle, source, columns: spec.sections[0] ? spec.sections[0].columns : [],
        formats: Object.keys(formats).filter(k => formats[k]), channels: Object.keys(channels).filter(k => channels[k]),
        emailTo, waTo, message, frequency, run_time: runTime, weekday, day_of_month: dom,
      };
      if (existing) {
        const r = await fetch(`${API}/reports/schedules/${existing.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` }, body: JSON.stringify({ ...body, email_to: emailTo, wa_to: waTo }) });
        if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
        setDone(await r.json());
      } else {
        setDone(await (await post('/reports/schedules', body)).json());
      }
      localStorage.setItem('dj_last_email_to', emailTo); localStorage.setItem('dj_last_wa_to', waTo);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Modal title={`SCHEDULE · ${spec.title}`} onClose={onClose}>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
        Runs on the server and emails itself automatically. It keeps the filters you have applied right now:
        <b> {spec.subtitle || 'no filters — the full list'}</b>.
      </div>
      <div style={label}>Frequency</div>
      {['daily', 'weekly', 'monthly'].map(f => <Chip key={f} on={frequency === f} onClick={() => setFrequency(f)}>{f.toUpperCase()}</Chip>)}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}><div style={label}>Time (IST)</div><input type="time" style={field} value={runTime} onChange={e => setRunTime(e.target.value)} /></div>
        {frequency === 'weekly' && <div style={{ flex: 1 }}><div style={label}>Day</div>
          <select style={field} value={weekday} onChange={e => setWeekday(+e.target.value)}>{days.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></div>}
        {frequency === 'monthly' && <div style={{ flex: 1 }}><div style={label}>Day of month</div>
          <select style={field} value={dom} onChange={e => setDom(+e.target.value)}>{Array.from({ length: 28 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}</select></div>}
      </div>
      {SHOW_WHATSAPP && <>
        <div style={label}>Deliver via</div>
        <Chip on={channels.email} onClick={() => setChannels(c => ({ ...c, email: !c.email }))}>✉️ Email</Chip>
        <Chip on={channels.whatsapp} onClick={() => setChannels(c => ({ ...c, whatsapp: !c.whatsapp }))}>🟢 WhatsApp</Chip>
      </>}
      <div style={label}>Attach</div>
      <Chip on={formats.pdf} onClick={() => setFormats(f => ({ ...f, pdf: !f.pdf }))}>📄 PDF</Chip>
      <Chip on={formats.xlsx} onClick={() => setFormats(f => ({ ...f, xlsx: !f.xlsx }))}>📊 Excel</Chip>
      {channels.email && <><div style={label}>Email to</div><input style={field} value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="name@company.com, …" /></>}
      {channels.whatsapp && <>
        <div style={label}>WhatsApp numbers</div>
        <input style={field} value={waTo} onChange={e => setWaTo(e.target.value)} placeholder="98xxxxxxxx, …" />
        <RecipientPicker people={people} phone onPick={v => setWaTo(cur => addTo(cur, v))} />
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Until a WhatsApp sender is linked for Rishab, scheduled WhatsApp prepares the message + file and pushes you a “tap to send” alert at the scheduled time (see ALERTS → Schedules).</div>
      </>}
      <div style={label}>Message (optional)</div>
      <textarea style={{ ...field, minHeight: 50 }} value={message} onChange={e => setMessage(e.target.value)} />
      {err && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 8 }}>{err}</div>}
      {done && <div style={{ fontSize: 12, marginTop: 8 }}>✅ Saved. Next run: {done.next_run_at ? new Date(done.next_run_at).toLocaleString('en-IN') : '—'}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button style={btn()} onClick={onClose}>{done ? 'CLOSE' : 'CANCEL'}</button>
        {!done && <button style={btn(true)} disabled={busy || (!channels.email && !channels.whatsapp)} onClick={go}>{busy ? 'SAVING…' : existing ? 'UPDATE' : 'SAVE SCHEDULE'}</button>}
      </div>
    </Modal>
  );
}
